import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslate } from "../useTranslate";
import { useSelector } from "react-redux";
import Icon from "./icon";
import Avatar from "./avatar";
import Can from "../can";
import BrandLogo from "./brand-logo";
import {ADMIN_PERMISSIONS, hasAdminPermission, hasAdminRoleAccess, isSuperAdmin} from "../admin-access";

function Sidebar({ onChange, expanded, extraPages = [] }) {
    const { config, pages, homePageIndex } = useSelector((state) => state.app);
    const {user} = useSelector(state => state.auth);

    const navigate = useNavigate();
    const location = useLocation();
    const translate = useTranslate();

    const [currentPageIndex, setCurrentPageIndex] = useState(homePageIndex);
    // Merge configured and runtime pages with identical behavior.
    const mergedPages = useMemo(() => {
        const base = Array.isArray(pages) ? pages : [];
        const extra = Array.isArray(extraPages) ? extraPages : [];

        // Runtime pages override configured pages with the same name.
        const byName = new Map();
        base.forEach(p => p?.name && byName.set(p.name, p));
        extra.forEach(p => p?.name && byName.set(p.name, p));

        return Array.from(byName.values());
    }, [pages, extraPages]);

    // Resolve the active page from the current route.
    useEffect(() => {
        // console.log(`/${location.pathname.split("/")[1]}`)
        const idx =
            location.pathname === "/"
                ? homePageIndex
                : mergedPages.findIndex(e => location.pathname === e["nav-url"]);

        setCurrentPageIndex(idx === -1 ? homePageIndex : idx);
    }, [location.pathname, mergedPages, homePageIndex]);

    // Keep the browser title in sync with the active page.
    useEffect(() => {
        const p = mergedPages[currentPageIndex];
        if (p) {
            document.title = `${translate(config["app-name"])} - ${translate(p.name)}`;
        } else {
            document.title = `${translate(config["app-name"])}`;
        }
    }, [currentPageIndex, mergedPages, config, translate]);

    return (
        <div className={`${expanded ? "w-16rem" : "w-6rem"} transition-ease-in-out transition-duration-400 h-full border-right-1 border-left-1 border-700 flex flex-column overflow-y-auto scrollbar-hide overflow-x-hidden surface-900`}>
            {/* Header */}
            <div className={`flex p-2 align-items-center ${!expanded && "justify-content-center"} border-bottom-1 border-700 h-4rem`}>
                <BrandLogo variant="mark" className="w-3rem h-3rem cursor-pointer" onClick={() => navigate("/")}/>

                {expanded && (
                    <div className="flex flex-column pl-2 pr-2">
                        <span className="text-2xs w-10rem text-gray-400">
                            {translate("app-description")}
                        </span>
                    </div>
                )}
            </div>

            {/* Items */}
            <div className={`w-full h-full ${expanded?"p-3":"p-2 pr-0"} pl-0 gap-2 flex flex-column flex-1 overflow-hidden`}>
                <div className="flex flex-column w-full overflow-auto h-full scrollbar-hide text-500">
                    {mergedPages.map((e, index) => (
                        e["show-in-sidebar"]
                        && !e["is-group"]
                        && (!e.can || hasAdminPermission(user, e.can))
                        && (!e["admin-roles"] || hasAdminRoleAccess(user, e["admin-roles"]))
                        && (!e["super-admin-only"] || isSuperAdmin(user))
                        && (
                            <Can key={e.name} permission={e.can}>
                                    <SidebarItem
                                        expanded={expanded}
                                        icon={e.icon}
                                        disabled={e.disabled}
                                        link={e.link}
                                        label={e.name}
                                        selected={`/${location.pathname.split("/")[1]}` === e["nav-url"]}
                                        onClick={() => {
                                            navigate(e["nav-url"]);
                                            setCurrentPageIndex(index);
                                            onChange && onChange({ index });
                                        }}
                                    />
                            </Can>
                        )
                    ))}
                </div>
            </div>
            <div className={`flex flex-column mt-auto pb-2 text-500 ${expanded?"p-3":"p-2 pr-0"} pt-0 pl-0 border-top-1 border-700`}>

                {/* Settings */}
                {hasAdminPermission(user, ADMIN_PERMISSIONS.SETTINGS_ACCESS) && (
                    <SidebarItem
                        expanded={expanded}
                        icon={"mi mi:settings"}
                        disabled={false}
                        label={"settings"}
                        selected={`/${location.pathname.split("/")[1]}`=== "/settings"}
                        onClick={() => navigate("/settings")}
                    />
                )}

                {/* User Account (Avatar icon) */}
                <SidebarItem
                    expanded={expanded}
                    avatar={
                        <Avatar memberData={user} bgColorRange="500" borderColorRange={0} className="text-900 text-2xs font-bold"/>
                    }
                    disabled={true}
                    label={user.fullname}
                    selected={`/${location.pathname.split("/")[1]}` === "/user-account"}
                    onClick={() => navigate("/user-account")}
                />
            </div>
        </div>
    );
}
function SidebarItem({
                         label,
                         selected,
                         onClick,
                         disabled,
                         icon,
                         avatar,
                         expanded,
                         link,
                         isChild,
                     }) {
    const translate = useTranslate();

    const ItemContent = (
        <div className={`
        ${expanded?"p-3":"p-2"}
        ${isChild ? (expanded ? "pl-5 pr-3" : "pl-2") : "pl-3 pr-3"}
        border-round-lg
        
        w-full
        cursor-pointer
        text-sm
        flex
        ${expanded?"flex-row":"flex-column text-2xs"}
        align-items-center
        gap-2
        white-space-nowrap
        ${!expanded && "justify-content-center"}
        ${selected ? "text-50" : "hover:text-400"}
        ${disabled && "p-disabled"}
      `}
            style={{
                fontSize: isChild ? "0.82rem" : "0.9rem",
                opacity: isChild ? 0.85 : 1,
                position: "relative"
            }}
            onClick={!link ? onClick : undefined}>
            {icon && <Icon icon={icon} />}
            {avatar}
            {<span>{translate(label)}</span>}
            {selected && (
                <span
                    style={{
                        position: "absolute",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "6px",
                        height: expanded?"70%":"50%",
                        background: "#ff6a00",
                        borderTopRightRadius: "8px",
                        borderBottomRightRadius: "8px",
                        left: "-1px",
                        pointerEvents: "none"
                    }}
                />
            )}
        </div>
    );

    if (link) {
        return (
            <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline text-color"
            >
                {ItemContent}
            </a>
        );
    }

    return ItemContent;
}

export default Sidebar;
